import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LifeBuoy, X, Sparkles, Mic, ShieldCheck, BookOpen, Search, MessageSquarePlus } from "lucide-react";
import { openTool } from "@/lib/dockBus";

// Hides the old individual floating launchers — they're now reached via this dock.
const HIDE_CSS = `
[data-testid="chat-widget-open"],
[data-testid="feedback-button"],
[data-testid="safety-button"],
[data-testid="page-guide-launcher"],
[data-testid="search-fab"],
[data-testid="pula-voice-launcher"],
[data-testid="pula-voice-nudge"] { display: none !important; }
`;

// One calm, unobtrusive "Help & Safety" dock that consolidates the 6 floating tools.
export function HelpDock() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const items = [
    { id: "halle", name: "Ask Halle AI", desc: "Chat in your language", Icon: Sparkles, action: () => openTool("halle") },
    { id: "voice", name: "Voice Assistant", desc: "Speak or listen", Icon: Mic, action: () => openTool("voice") },
    { id: "safety-center", name: "Safety & Emergency", desc: "Guides & report", Icon: ShieldCheck, action: () => openTool("safety") },
    { id: "page-guide", name: "Page Guide", desc: "How to use this page", Icon: BookOpen, action: () => openTool("guide") },
    { id: "search-fab", name: "Quick Search", desc: "Find anything", Icon: Search, action: () => navigate("/search") },
    { id: "feedback-corner", name: "Send Feedback", desc: "Tell us how to improve", Icon: MessageSquarePlus, action: () => openTool("feedback") },
  ];

  const pick = (action) => { action(); setOpen(false); };

  return (
    <>
      <style>{HIDE_CSS}</style>
      <div className="fixed bottom-6 right-6 z-[70] flex flex-col items-end gap-3" data-testid="help-dock" data-simple-hide="true">
        {open && (
          <div data-testid="help-dock-menu"
            className="w-72 rounded-2xl border border-[var(--pula-border)] bg-white shadow-2xl overflow-hidden origin-bottom-right"
            style={{ animation: "pulaDockIn 160ms ease-out" }}>
            <div className="px-4 py-3 bg-pula-ink text-white flex items-center justify-between">
              <span className="font-heading font-bold text-sm inline-flex items-center gap-2"><LifeBuoy className="w-4 h-4 text-pula-green" /> Help &amp; Tools</span>
              <button onClick={() => setOpen(false)} data-testid="help-dock-close" aria-label="Close help menu" className="w-6 h-6 grid place-items-center rounded-full hover:bg-white/15">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-2">
              {items.map((it) => (
                <button key={it.id} data-testid={`help-dock-item-${it.id}`} onClick={() => pick(it.action)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-pula-bg2 transition-colors text-left">
                  <span className="w-9 h-9 rounded-full bg-pula-green/12 grid place-items-center text-pula-green shrink-0"><it.Icon className="w-4 h-4" /></span>
                  <span className="min-w-0">
                    <span className="block font-heading font-semibold text-sm text-pula-ink">{it.name}</span>
                    <span className="block text-xs text-pula-inksoft font-body">{it.desc}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
        <button data-testid="help-dock-trigger" onClick={() => setOpen(!open)} aria-label="Help and safety tools"
          className="inline-flex items-center gap-2 rounded-full bg-pula-green hover:bg-pula-greenhover text-white shadow-lg shadow-black/20 pl-4 pr-5 py-3 transition-colors">
          {open ? <X className="w-5 h-5" /> : <LifeBuoy className="w-5 h-5" />}
          <span className="text-sm font-semibold">Help &amp; Safety</span>
        </button>
      </div>
      <style>{`@keyframes pulaDockIn{from{opacity:0;transform:translateY(8px) scale(.97)}to{opacity:1;transform:none}}`}</style>
    </>
  );
}
